
/**
 * Safe initialization utilities for ESM and CommonJS modules
 * This helps avoid TDZ (Temporal Dead Zone) issues
 */

/**
 * Safely initializes a module by wrapping initialization in try/catch
 * 
 * @param initFn Function to run for initialization
 * @param fallbackFn Function to run if initialization fails
 * @returns Result of initialization or fallback
 */
export function safeInit<T>(initFn: () => T, fallbackFn: () => T): T {
  try {
    return initFn();
  } catch (error) {
    console.warn('Module initialization failed, using fallback', error);
    return fallbackFn();
  }
}

/**
 * Safely loads a module asynchronously
 * 
 * @param importPromise Dynamic import promise
 * @param fallback Fallback value if import fails
 * @returns Promise resolving to module or fallback
 */
export async function safeImport<T>(importPromise: Promise<T>, fallback: T): Promise<T> {
  try {
    return await importPromise;
  } catch (error) {
    console.warn('Module import failed, using fallback', error);
    return fallback;
  }
}

/**
 * Creates a safe wrapper around a React component to prevent runtime errors
 * 
 * @param Component The React component to wrap
 * @returns Safe component that won't crash if props are invalid
 */
export function createSafeComponent(Component: any) {
  return function SafeComponent(props: any) {
    try {
      return Component(props);
    } catch (error) {
      console.error('Component rendering error:', error);
      return null;
    }
  };
}

/**
 * Helper to safely initialize console to prevent errors in environments where console might be restricted
 */
export function initializeConsoleVisibility() {
  try {
    // Check if console exists and is fully functional
    if (typeof console === 'undefined') {
      // Create minimal console implementation if needed
      (window as any).console = {
        log: function() {},
        warn: function() {},
        error: function() {}
      };
    }
    
    // Add Lovable-specific console methods
    console.lovable = {
      info: function(...args: any[]) {
        console.log('[Lovable]', ...args);
      }
    };
    
    return true;
  } catch (e) {
    // Last resort fallback
    return false;
  }
}
